// timelineRangeFilter.js - Shared timeline range-filtering logic.
// Extracted from BubbleChart.jsx / ActivityBubbleChart.jsx, which previously
// each re-implemented "take the last N sessions" filtering independently.
//
// timelineData is expected to be an array of day entries (newest day first),
// each shaped like { date, display_date, sessions: [...] }, as produced by
// PastSessions.loadTimelineData / the /share/chart backend response.

/**
 * Filters a timeline (array of day entries) down to a range, in one of two
 * modes:
 *  - 'sessions' (default): keep only the most recent N individual sessions,
 *    regardless of how many days they span.
 *  - 'days': keep only the most recent N calendar days that have sessions,
 *    regardless of how many sessions occurred on those days.
 *
 * `range` of 'max' (or an invalid/non-positive count) returns the timeline
 * unchanged in either mode, preserving existing default behavior.
 *
 * @param {Array} timelineData
 * @param {string} range - e.g. '5', '15', 'max'
 * @param {'sessions'|'days'} [mode='sessions']
 * @returns {Array} filtered day entries, in the same relative order as timelineData
 */
export function filterTimelineByRange(timelineData, range, mode = 'sessions') {
  if (!timelineData || !Array.isArray(timelineData)) return [];
  if (range === 'max') return timelineData;

  const count = parseInt(range, 10);
  if (isNaN(count) || count <= 0) return timelineData;

  const daysWithSessions = timelineData.filter((d) => d.sessions && d.sessions.length > 0);

  if (mode === 'days') {
    return daysWithSessions.slice(0, count);
  }

  // 'sessions' mode: take the last N sessions (newest first, as timelineData
  // is already ordered), then regroup by day while preserving the original
  // chronological order of days from timelineData.
  const allSessions = [];
  for (const dayEntry of daysWithSessions) {
    for (const session of dayEntry.sessions) {
      allSessions.push({ ...session, _dayDate: dayEntry.date, _dayDisplay: dayEntry.display_date });
    }
  }

  const lastSessions = allSessions.slice(0, count);

  const dayMap = new Map();
  for (const session of lastSessions) {
    const date = session._dayDate;
    if (!dayMap.has(date)) {
      const originalDay = daysWithSessions.find((d) => d.date === date);
      dayMap.set(date, {
        date,
        display_date: originalDay?.display_date || session._dayDisplay,
        sessions: [],
      });
    }
    const { _dayDate, _dayDisplay, ...cleanSession } = session;
    dayMap.get(date).sessions.push(cleanSession);
  }

  // Preserve original chronological order from timelineData.
  const result = [];
  for (const dayEntry of daysWithSessions) {
    if (dayMap.has(dayEntry.date)) {
      result.push(dayMap.get(dayEntry.date));
    }
  }
  return result;
}
